import React, {useState, useContext, useEffect} from "react";
import clsx from "clsx";

import {
    makeStyles,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    MenuItem,
    IconButton,
    Menu,
} from "@material-ui/core";

import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsChatPopOver from "../components/NotificationsChatPopOver";
import UserModal from "../components/UserModal";
import {AuthContext} from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import {i18n} from "../translate/i18n";
import PathNotesModal from "../components/PatchNotesModal";
import HelpIcon from '@material-ui/icons/MenuBook';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import NotificationsUserPopOver from "../components/NotificationsUserPopOver";
import NoticeModal from "../components/NoticeModal";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        height: "100vh",
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 56px)",
        },
    },

    toolbar: {
        paddingRight: 20,
        backgroundColor: theme.topbar.primary,
        textAlign: "center",
        color: "#fff",
    },
    toolbarIcon: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: "0 8px",
        minHeight: "48px",
        color: "#fff",
        backgroundColor: theme.sidebar.primary,
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    menuButton: {
        marginRight: 36,
        color: "#fff",
    },
    menuButtonHidden: {
        display: "none",
    },
    title: {
        flexGrow: 1,
    },
    toolbarDivider: {
        width: 1,
        height: 28,
        margin: "0 8px",
        flexShrink: 0,
        backgroundColor: "rgba(255, 255, 255, 0.8)",
    },
    drawerPaper: {
        position: "relative",
        whiteSpace: "nowrap",
        width: drawerWidth,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        backgroundColor: theme.sidebar.primary,
        color: "#fff",
    },
    drawerPaperClose: {
        overflowX: "hidden",
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up("sm")]: {
            width: theme.spacing(9),
        },
        backgroundColor: theme.sidebar.primary,
        color: "#fff",
    },
    appBarSpacer: {
        minHeight: "48px",
    },
    content: {
        flex: 1,
        overflow: "auto",
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: "flex",
        overflow: "auto",
        flexDirection: "column",
    },
    version: {
        marginTop: "auto",
        textAlign: "center"
    },
    scrollbar: {
        overflowY: "auto",
        overflowX: "hidden",
        ...theme.scrollbarStyles,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
            width: 0,
            height: 0,
        },
    }
}));

const LoggedInLayout = ({children}) => {
    const classes = useStyles();
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const {handleLogout, loading} = useContext(AuthContext);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerVariant, setDrawerVariant] = useState("permanent");
    const [pathNotesModalOpen, setPathNotesModalOpen] = useState(false);
    const [noticeModalOpen, setNoticeModalOpen] = useState(false);
    const {user} = useContext(AuthContext);
    const projectName = (window.env?.REACT_APP_NAME || process.env.REACT_APP_NAME) || "WhatsContábil";

    useEffect(() => {
        if (document.body.offsetWidth > 600) {
            setDrawerOpen(false);
        }
    }, []);

    useEffect(() => {
        if (document.body.offsetWidth < 600) {
            setDrawerVariant("temporary");
        } else {
            setDrawerVariant("permanent");
        }
    }, [drawerOpen]);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handleOpenPathNotes = (event) => {
        setPathNotesModalOpen(true)
    };

    const handleOpenNotices = (event) => {
        setNoticeModalOpen(true)
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const handleOpenUserModal = () => {
        setUserModalOpen(true);
        handleCloseMenu();
    };

    const handleClickLogout = () => {
        handleCloseMenu();
        handleLogout();
    };

    const drawerClose = () => {
        if (document.body.offsetWidth < 600) {
            setDrawerOpen(false);
        }
    };

    if (loading) {
        return <BackdropLoading/>;
    }

    return (
        <div className={classes.root}>
            <Drawer
                variant={drawerVariant}
                className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
                classes={{
                    paper: clsx(
                        classes.drawerPaper,
                        !drawerOpen && classes.drawerPaperClose
                    ),
                }}
                open={drawerOpen}
            >
                <div className={classes.toolbarIcon}>
                    <IconButton style={{color: "#f3f3f3"}} onClick={() => setDrawerOpen(!drawerOpen)}>
                        <ChevronLeftIcon/>
                    </IconButton>
                </div>
                <Divider/>
                <List className={classes.scrollbar}>
                    <MainListItems
                        drawerClose={drawerClose}
                        drawerOpen={drawerOpen}
                        drawerOpenMenu={() => setDrawerOpen(true)}
                    />
                </List>
                <Divider style={{backgroundColor: "#fcfcfc"}}/>
                <div className={classes.version}>
                    <Typography style={{fontSize: "11pt"}}>v7.1.4</Typography>
                </div>
            </Drawer>

            <UserModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                userId={user?.id}
            />

            <PathNotesModal
                modalOpen={pathNotesModalOpen}
                onClose={(e) => setPathNotesModalOpen(false)}
            />

            <NoticeModal
                modalOpen={noticeModalOpen}
                onClose={(e) => setNoticeModalOpen(false)}
            />

            <AppBar
                position="absolute"
                className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
                color={process.env.NODE_ENV === "development" ? "inherit" : "primary"}
            >
                <Toolbar variant="dense" className={classes.toolbar}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={() => setDrawerOpen(!drawerOpen)}
                        className={clsx(
                            classes.menuButton,
                            drawerOpen && classes.menuButtonHidden
                        )}
                    >
                        <MenuIcon/>
                    </IconButton>
                    <Typography
                        component="h1"
                        variant="h6"
                        color="inherit"
                        noWrap
                        className={classes.title}
                    >
                        {projectName ?? "WhatsContábil"}
                    </Typography>

                    {user.id && (<NotificationsUserPopOver/>)}

                    {user.id && <NotificationsChatPopOver/>}

                    {user.id && <NotificationsPopOver/>}

                    <span className={classes.toolbarDivider} aria-hidden="true" />

                    <div>
                        <IconButton
                            title={"Notas de atualização"}
                            aria-label="Notas de atualização"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenPathNotes}
                            color="inherit"
                        >
                            <HelpIcon/>
                        </IconButton>
                    </div>

                    <div>
                        <IconButton
                            title={"Comunicados"}
                            aria-label="Comunicados"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNotices}
                            color="inherit"
                        >
                            <AnnouncementIcon />
                        </IconButton>
                    </div>

                    <div>
                        <IconButton
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                            color="inherit"
                        >
                            <AccountCircle/>
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            getContentAnchorEl={null}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={menuOpen}
                            onClose={handleCloseMenu}
                        >
                            <MenuItem onClick={handleOpenUserModal}>
                                {i18n.t("mainDrawer.appBar.user.profile")}
                            </MenuItem>
                            <MenuItem onClick={handleClickLogout}>
                                {i18n.t("mainDrawer.appBar.user.logout")}
                            </MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>
            <main className={classes.content}>
                <div className={classes.appBarSpacer}/>

                {children ? children : null}
            </main>
        </div>
    );
};

export default LoggedInLayout;
